import type { AssetDiagnosticHistoryItemResponseDto } from '@evimo/types';

export const DIAGNOSTICS_HISTORY_PAGE_SIZE = 5;

export type DiagnosticsStatusTagColor =
  | 'default'
  | 'processing'
  | 'success'
  | 'warning'
  | 'error'
  | 'blue'
  | 'cyan'
  | 'orange'
  | 'geekblue';

export function diagnosticsStatusTagColor(
  status: string,
): DiagnosticsStatusTagColor {
  switch (status) {
    case 'REQUESTED':
      return 'blue';
    case 'ACCEPTED':
      return 'cyan';
    case 'UPLOADING':
      return 'processing';
    case 'UPLOADED':
      return 'success';
    case 'REJECTED':
    case 'UPLOAD_FAILED':
      return 'error';
    case 'EXPIRED':
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
}

export function diagnosticsDisplayFileName(
  row: AssetDiagnosticHistoryItemResponseDto,
): string | null {
  return row.ocppFileName?.trim() || null;
}

export function diagnosticsUploadedWithoutFile(
  row: AssetDiagnosticHistoryItemResponseDto,
): boolean {
  return row.ocppStatus === 'Uploaded' && !row.downloadable;
}

/**
 * OCPP telemetry subtitle. Hidden once EVIMO lifecycle is UPLOADED /
 * downloadable so stale values like "Uploading" are not shown as current state.
 */
export function diagnosticsShouldShowOcppStatus(
  row: Pick<
    AssetDiagnosticHistoryItemResponseDto,
    'status' | 'ocppStatus' | 'downloadable'
  >,
): boolean {
  if (!row.ocppStatus) {
    return false;
  }
  if (row.status === 'UPLOADED' || row.downloadable) {
    return false;
  }
  return true;
}

export function diagnosticsHistoryHasNonTerminal(
  rows: readonly AssetDiagnosticHistoryItemResponseDto[],
): boolean {
  return rows.some(
    (row) =>
      row.status === 'REQUESTED' ||
      row.status === 'ACCEPTED' ||
      row.status === 'UPLOADING',
  );
}

/** Operator cancel is allowed only while waiting (not once upload started). */
export function diagnosticsIsCancellable(
  row: Pick<AssetDiagnosticHistoryItemResponseDto, 'status'>,
): boolean {
  return row.status === 'REQUESTED' || row.status === 'ACCEPTED';
}

export function formatDiagnosticsRequestedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString();
}
